import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box, Typography, TextField, Button, Table, TableHead, TableBody, TableRow,
  TableCell, TableSortLabel, TableContainer, TablePagination, CircularProgress,
  IconButton, InputAdornment, Chip, Tooltip, Avatar, Skeleton, Paper,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import recruiterTheme, { FONT_BODY, FONT_DISPLAY, TYPE, RADIUS, SHADOW, ELLIPSIS, PAGE_PADDING } from "../../styles/recruiterTheme";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import GroupsIcon from "@mui/icons-material/Groups";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InboxIcon from "@mui/icons-material/Inbox";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

const REPORT_API = process.env.REACT_APP_API_BASE_URL;

// ─── Animations ─────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Page wrapper ───────────────────────────────────────────────────────────
// Standalone route: own canvas + PAGE_PADDING. Inside a dashboard tab the host
// already provides the canvas and padding, so this adds none.
const PageWrapper = styled.div`
  font-family: ${FONT_BODY};
  width: 100%;
  box-sizing: border-box;
  ${(p) => (p.$standalone ? `background: #F3EEF9; min-height: 100vh; padding: ${PAGE_PADDING}px;` : "")}
`;

// ─── Header ─────────────────────────────────────────────────────────────────
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const BackBtn = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #F3EEF9, #E8DEF8);
    border-radius: ${RADIUS.sm}px;
    width: 40px; height: 40px;
    flex-shrink: 0;
    transition: background 0.2s;
    &:hover {
      background: linear-gradient(135deg, #E8DEF8, #D0BCFF);
    }
    svg { color: #4F378B; font-size: 20px; }
  }
`;

const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #1A0A3D 0%, #6750A4 50%, #9C7FD4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: #8B7DB5 !important;
    font-weight: 500 !important;
    margin-top: 4px !important;
  }
`;

// ─── Stat cards ─────────────────────────────────────────────────────────────
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  animation: ${fadeSlideIn} 0.4s ease both;
`;

const StatCard = styled.div`
  background: ${(p) => p.gradient || "linear-gradient(135deg,#6750A4,#4F378B)"};
  border-radius: ${RADIUS.md}px;
  padding: 16px;
  min-height: 88px;
  height: 100%;
  box-sizing: border-box;
  color: #fff;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: 0 4px 16px ${(p) => p.shadow || "rgba(103,80,164,0.26)"};
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 24px ${(p) => p.shadow || "rgba(103,80,164,0.36)"}; }
  &::after {
    content: '';
    position: absolute;
    top: -24px; right: -24px;
    width: 72px; height: 72px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatNum = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const StatLbl = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  line-height: 1.25;
  opacity: 0.9;
`;

const StatIconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  opacity: 0.35;
  svg { font-size: 32px; }
`;

// ─── Filter / content cards ─────────────────────────────────────────────────
const FilterCard = styled(Paper)`
  && {
    border-radius: ${RADIUS.md}px !important;
    padding: 24px !important;
    box-shadow: ${SHADOW.card} !important;
    border: 1px solid #EDE7F6;
    background: #fff !important;
    margin: 0 0 24px 0;
    width: 100%;
    box-sizing: border-box;
  }
`;

const TableCard = styled(Paper)`
  && {
    border-radius: ${RADIUS.md}px !important;
    box-shadow: ${SHADOW.card} !important;
    border: 1px solid #EDE7F6;
    background: #fff !important;
    overflow: hidden;
    margin: 0 0 24px 0;
    width: 100%;
    box-sizing: border-box;
    animation: ${fadeSlideIn} 0.45s ease 0.05s both;
  }
`;

const CardHeaderRow = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  border-bottom: 2px solid #F0EAF9;
`;

const CardTitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.cardTitle}px !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
  }
`;

const CardMeta = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.caption}px !important;
    color: #8B7DB5 !important;
    font-weight: 600 !important;
    margin-top: 4px !important;
  }
`;

const ApplyBtn = styled.button`
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.small}px;
  border-radius: ${RADIUS.sm}px;
  padding: 0 16px;
  height: 40px;
  min-width: 120px;
  border: none;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(135deg, #6750A4, #4F378B);
  box-shadow: 0 3px 10px rgba(103,80,164,0.28);
  transition: all 0.18s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(103,80,164,0.38); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const GhostBtn = styled.button`
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.small}px;
  border-radius: ${RADIUS.sm}px;
  padding: 0 16px;
  height: 40px;
  border: 1.5px solid #C4B0E8;
  cursor: pointer;
  color: #6750A4;
  background: transparent;
  transition: all 0.18s ease;
  &:hover { background: #F3EEF9; }
`;

const ExportBtn = styled.button`
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.small}px;
  border-radius: ${RADIUS.sm}px;
  padding: 0 16px;
  height: 40px;
  border: none;
  cursor: pointer;
  color: #6750A4;
  background: #EDE7F6;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  transition: background 0.18s;
  &:hover { background: #D0BCFF; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ─── Table styling ──────────────────────────────────────────────────────────
const StyledTableContainer = styled(TableContainer)`
  && {
    overflow-x: auto;
    .MuiTableCell-root {
      padding: 12px 16px;
      vertical-align: middle;
    }
    .MuiTableCell-head {
      font-family: ${FONT_BODY};
      font-weight: 700;
      font-size: ${TYPE.overline}px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      white-space: nowrap;
      color: #8B7DB5;
      background: #FAF7FF;
      border-bottom: 2px solid #F0EAF9;
    }
    .MuiTableCell-body {
      font-family: ${FONT_BODY};
      border-bottom: 1px solid #F5F1FB;
    }
    .MuiTableRow-root:hover .MuiTableCell-body {
      background: #FAF7FF;
    }
  }
`;

// ─── Status-style chips (reused from CandidateData's chip language) ─────────
const CountChipStyled = styled(Chip)`
  && {
    font-family: ${FONT_BODY};
    font-weight: 700;
    font-size: ${TYPE.caption}px;
    font-variant-numeric: tabular-nums;
    min-width: 40px;
    height: 24px;
    border-radius: ${RADIUS.sm}px;
  }
`;

const AVATAR_PALETTE = ["#6750A4", "#1565C0", "#2D7D46", "#E65100", "#B3261E", "#7C5CBF", "#1D4ED8"];

function colorForName(name = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

function initialsOf(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function NameCell({ name, subtitle }) {
  const bg = colorForName(name);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
      <Avatar sx={{ width: 32, height: 32, fontSize: TYPE.caption, fontWeight: 700, bgcolor: bg, fontFamily: FONT_BODY, flexShrink: 0 }}>
        {initialsOf(name)}
      </Avatar>
      <Box sx={{ minWidth: 0, maxWidth: 320 }}>
        <Typography title={name} sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.small, lineHeight: 1.5, color: "#2D1B5E", ...ELLIPSIS }}>
          {name}
        </Typography>
        {subtitle && (
          <Typography title={subtitle} sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: "#8B7DB5", fontWeight: 500, ...ELLIPSIS }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function CountChip({ value, tone }) {
  const styles = {
    success: { bg: "#E6F4EA", fg: "#2D7D46" },
    danger: { bg: "#FDECEA", fg: "#B3261E" },
    warning: { bg: "#FFF8E1", fg: "#E65100" },
    neutral: { bg: "#EDE7F6", fg: "#4F378B" },
  }[tone];
  return <CountChipStyled label={value} size="small" sx={{ bgcolor: styles.bg, color: styles.fg }} />;
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <Box sx={{ py: 5, px: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <Box sx={{
        width: 56, height: 56, borderRadius: "50%", bgcolor: "#F3EEF9",
        display: "flex", alignItems: "center", justifyContent: "center", mb: 1,
      }}>
        {icon}
      </Box>
      <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, lineHeight: 1.25, color: "#2D1B5E" }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{
          fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: "#8B7DB5",
          maxWidth: 360, textAlign: "center", fontWeight: 500,
        }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// Error banner shown above the report when a request fails.
const errorBannerSx = {
  border: "1.5px solid #F5C6CB", background: "#FDECEA", borderRadius: `${RADIUS.md}px`,
  p: 2, fontFamily: FONT_BODY, color: "#B3261E", fontWeight: 600, fontSize: TYPE.body, lineHeight: 1.5,
};

// ─── CSV helpers ────────────────────────────────────────────────────────────
function toCsv(headers, rows) {
  const escape = (val) => {
    const s = String(val ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(escape).join(",")];
  for (const row of rows) lines.push(row.map(escape).join(","));
  return lines.join("\n");
}

function downloadCsv(filename, csvContent) {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function descComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descComparator(a, b, orderBy)
    : (a, b) => -descComparator(a, b, orderBy);
}

// Shared textfield look so it matches the InfoCard/dialog inputs on CandidateData
const dateFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: `${RADIUS.sm}px`,
    fontFamily: FONT_BODY,
    height: 40,
  },
  "& .MuiInputLabel-root": { fontFamily: FONT_BODY },
};

export default function ClientSummaryReport() {
  // Own page padding only on the standalone /reports/client-summary route.
  const { pathname } = useLocation();
  const standalone = pathname.startsWith("/reports/client-summary");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [allClients, setAllClients] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [errorAll, setErrorAll] = useState(null);

  const [selectedClient, setSelectedClient] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [errorDetail, setErrorDetail] = useState(null);

  const [search, setSearch] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("jds_released");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);

  const runAllClientsReport = useCallback(async (from = dateFrom, to = dateTo) => {
    setErrorAll(null);
    setLoadingAll(true);
    setSelectedClient(null);
    setDetail(null);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      const res = await fetch(`${REPORT_API}/reports/all-clients-summary?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Failed to load report");
      setAllClients(data.clients || []);
      setPage(0);
    } catch (err) {
      setErrorAll(err.message || "Failed to load report");
      setAllClients(null);
    } finally {
      setLoadingAll(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    runAllClientsReport("", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openClientDetail = useCallback(async (companyName, from = dateFrom, to = dateTo) => {
    setSelectedClient(companyName);
    setErrorDetail(null);
    setLoadingDetail(true);
    setDetail(null);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ company_name: companyName });
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      const res = await fetch(`${REPORT_API}/reports/client-summary?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Failed to load client detail");
      setDetail(data);
    } catch (err) {
      setErrorDetail(err.message || "Failed to load client detail");
    } finally {
      setLoadingDetail(false);
    }
  }, [dateFrom, dateTo]);

  const applyDateFilter = useCallback(() => {
    if (selectedClient) openClientDetail(selectedClient);
    else runAllClientsReport();
  }, [selectedClient, openClientDetail, runAllClientsReport]);

  const clearDateFilter = useCallback(() => {
    setDateFrom("");
    setDateTo("");
    if (selectedClient) openClientDetail(selectedClient, "", "");
    else runAllClientsReport("", "");
  }, [selectedClient, openClientDetail, runAllClientsReport]);

  const backToAllClients = useCallback(() => {
    setSelectedClient(null);
    setDetail(null);
    setErrorDetail(null);
  }, []);

  const downloadAllClientsCsv = useCallback(() => {
    if (!allClients) return;
    const csv = toCsv(
      ["Client", "JDs Released", "Selected", "Rejected", "In progress"],
      allClients.map((c) => [c.company_name, c.jds_released, c.selected, c.rejected, c.in_progress])
    );
    const range = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : "all_time";
    downloadCsv(`client_summary_${range}.csv`, csv);
  }, [allClients, dateFrom, dateTo]);

  const downloadClientDetailCsv = useCallback(() => {
    if (!detail) return;
    const csv = toCsv(
      ["Recruiter", "Email", "JDs Released", "Selected", "Rejected", "In progress"],
      detail.recruiter_breakdown.map((r) => [
        r.recruiter_name, r.recruiter_email, r.jds_released, r.selected, r.rejected, r.in_progress,
      ])
    );
    const safeClient = (selectedClient || "client").replace(/[^a-z0-9]+/gi, "_");
    const range = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : "all_time";
    downloadCsv(`${safeClient}_summary_${range}.csv`, csv);
  }, [detail, selectedClient, dateFrom, dateTo]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredSortedClients = useMemo(() => {
    if (!allClients) return [];
    const filtered = search.trim()
      ? allClients.filter((c) => c.company_name.toLowerCase().includes(search.trim().toLowerCase()))
      : allClients;
    return [...filtered].sort(getComparator(order, orderBy));
  }, [allClients, search, order, orderBy]);

  const paginatedClients = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredSortedClients.slice(start, start + rowsPerPage);
  }, [filteredSortedClients, page, rowsPerPage]);

  const totals = useMemo(() => {
    if (!allClients) return null;
    return allClients.reduce(
      (acc, c) => ({
        clients: acc.clients + 1,
        jds: acc.jds + (c.jds_released || 0),
        selected: acc.selected + (c.selected || 0),
        rejected: acc.rejected + (c.rejected || 0),
        in_progress: acc.in_progress + (c.in_progress || 0),
      }),
      { clients: 0, jds: 0, selected: 0, rejected: 0, in_progress: 0 }
    );
  }, [allClients]);

  const headSx = {
    fontFamily: FONT_BODY,
    fontWeight: 700,
    fontSize: TYPE.overline,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
    color: "#8B7DB5",
    background: "#FAF7FF",
  };

  const headerCell = (label, key, align = "right") => (
    <TableCell align={align} sx={headSx}>
      <TableSortLabel
        active={orderBy === key}
        direction={orderBy === key ? order : "desc"}
        onClick={() => handleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <ThemeProvider theme={recruiterTheme}>
    <PageWrapper $standalone={standalone}>
      {/* ── Header ── */}
      <HeaderBar>
        {selectedClient && (
          <BackBtn onClick={backToAllClients}>
            <ArrowBackIcon />
          </BackBtn>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: `${RADIUS.sm}px`,
              background: "linear-gradient(135deg, #F3EEF9, #E8DEF8)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <AssessmentIcon sx={{ fontSize: 20, color: "#6750A4" }} />
            </Box>
            <PageTitle title={selectedClient || undefined}>
              {selectedClient ? selectedClient : "Client Hiring Summary"}
            </PageTitle>
          </Box>
          <PageSubtitle>
            {selectedClient
              ? "Recruiter-level breakdown for this client's activity."
              : "Track job releases, selections, and rejections across every client."}
          </PageSubtitle>
        </Box>
      </HeaderBar>

      {/* ── Date filter ── */}
      <FilterCard>
        {selectedClient && (
          <Typography sx={{
            fontFamily: FONT_BODY, fontSize: TYPE.caption, color: "#8B7DB5",
            fontWeight: 600, mb: 2,
          }}>
            Filtering {selectedClient}'s activity — clear the filter or go back to see every client again.
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            type="date" label="From" size="small" InputLabelProps={{ shrink: true }}
            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ ...dateFieldSx, minWidth: 200 }}
          />
          <TextField
            type="date" label="To" size="small" InputLabelProps={{ shrink: true }}
            value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ ...dateFieldSx, minWidth: 200 }}
          />
          <ApplyBtn onClick={applyDateFilter} disabled={loadingAll || loadingDetail}>
            {(loadingAll || loadingDetail) ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Apply filter"}
          </ApplyBtn>
          {(dateFrom || dateTo) && <GhostBtn onClick={clearDateFilter}>Clear</GhostBtn>}
        </Box>
      </FilterCard>

      {errorAll && (
        <Box sx={{ mb: 3 }}>
          <Box sx={errorBannerSx}>
            {errorAll}
          </Box>
        </Box>
      )}

      {/* ── KPI strip ── */}
      {totals && !selectedClient && (
        <StatsRow>
          <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
            <StatText>
              <StatNum>{totals.clients}</StatNum>
              <StatLbl>Clients</StatLbl>
            </StatText>
            <StatIconWrap><GroupsIcon /></StatIconWrap>
          </StatCard>
          <StatCard gradient="linear-gradient(135deg,#7C5CBF,#5B3FA0)" shadow="rgba(124,92,191,0.28)">
            <StatText>
              <StatNum>{totals.jds}</StatNum>
              <StatLbl>JDs released</StatLbl>
            </StatText>
            <StatIconWrap><DescriptionIcon /></StatIconWrap>
          </StatCard>
          <StatCard gradient="linear-gradient(135deg,#7C5CBF,#5B3FA0)" shadow="rgba(124,92,191,0.28)">
            <StatText>
              <StatNum>{totals.selected}</StatNum>
              <StatLbl>Selected</StatLbl>
            </StatText>
            <StatIconWrap><CheckCircleIcon /></StatIconWrap>
          </StatCard>
          <StatCard gradient="linear-gradient(135deg,#7C5CBF,#5B3FA0)" shadow="rgba(124,92,191,0.28)">
            <StatText>
              <StatNum>{totals.rejected}</StatNum>
              <StatLbl>Rejected</StatLbl>
            </StatText>
            <StatIconWrap><CancelIcon /></StatIconWrap>
          </StatCard>
          <StatCard gradient="linear-gradient(135deg,#7C5CBF,#5B3FA0)" shadow="rgba(124,92,191,0.28)">
            <StatText>
              <StatNum>{totals.in_progress}</StatNum>
              <StatLbl>In progress</StatLbl>
            </StatText>
            <StatIconWrap><HourglassEmptyIcon /></StatIconWrap>
          </StatCard>
        </StatsRow>
      )}

      {loadingAll && !allClients && (
        <TableCard sx={{ p: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: `${RADIUS.sm}px` }} />
            ))}
          </Box>
        </TableCard>
      )}

      {/* ── All-clients table ── */}
      {allClients && !selectedClient && (
        <TableCard>
          <CardHeaderRow>
            <Box>
              <CardTitle>All clients</CardTitle>
              <CardMeta>
                {dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : "All time"} · {filteredSortedClients.length} of {allClients.length}
              </CardMeta>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search clients"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                sx={{ width: 220, "& .MuiOutlinedInput-root": { borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY, height: 40 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#8B7DB5" }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title="Download the currently filtered clients as CSV">
                <span>
                  <ExportBtn onClick={downloadAllClientsCsv} disabled={!allClients.length}>
                    <DownloadIcon sx={{ fontSize: 16 }} /> Export CSV
                  </ExportBtn>
                </span>
              </Tooltip>
            </Box>
          </CardHeaderRow>

          {filteredSortedClients.length === 0 ? (
            <EmptyState
              icon={<InboxIcon sx={{ color: "#9C7FD4", fontSize: 28 }} />}
              title={search ? "No matching clients" : "No clients found"}
              subtitle={search
                ? "Try a different search term or clear the search box."
                : "Try widening the date range or clearing filters."}
            />
          ) : (
            <>
              <StyledTableContainer sx={{ maxHeight: 560 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Client</TableCell>
                      {headerCell("JDs released", "jds_released")}
                      {headerCell("Selected", "selected")}
                      {headerCell("Rejected", "rejected")}
                      {headerCell("In progress", "in_progress")}
                      <TableCell align="center" sx={headSx}>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedClients.map((c) => (
                      <TableRow
                        key={c.company_name}
                        hover
                        sx={{ cursor: "pointer", "&:last-child td": { border: 0 } }}
                        onClick={() => openClientDetail(c.company_name)}
                      >
                        <TableCell><NameCell name={c.company_name} /></TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.small, fontVariantNumeric: "tabular-nums", color: "#2D1B5E" }}>
                            {c.jds_released}
                          </Typography>
                        </TableCell>
                        <TableCell align="right"><CountChip value={c.selected} tone="success" /></TableCell>
                        <TableCell align="right"><CountChip value={c.rejected} tone="danger" /></TableCell>
                        <TableCell align="right"><CountChip value={c.in_progress} tone="warning" /></TableCell>
                        <TableCell align="center">
                          <Tooltip title="View recruiter breakdown">
                            <IconButton
                              size="small"
                              sx={{ color: "#6750A4", width: 32, height: 32 }}
                              onClick={(e) => { e.stopPropagation(); openClientDetail(c.company_name); }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </StyledTableContainer>
              <TablePagination
                component="div"
                count={filteredSortedClients.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[7, 10, 25, 50]}
                sx={{ borderTop: "2px solid #F0EAF9", fontFamily: FONT_BODY }}
              />
            </>
          )}
        </TableCard>
      )}

      {/* ── Selected-client detail ── */}
      {selectedClient && (
        <>
          {errorDetail && (
            <Box sx={{ mb: 3 }}>
              <Box sx={errorBannerSx}>
                {errorDetail}
              </Box>
            </Box>
          )}

          {loadingDetail ? (
            <>
              <StatsRow>
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={88} sx={{ borderRadius: `${RADIUS.md}px` }} />
                ))}
              </StatsRow>
              <TableCard sx={{ p: 3 }}>
                <Skeleton variant="rounded" height={240} sx={{ borderRadius: `${RADIUS.md}px` }} />
              </TableCard>
            </>
          ) : detail && (
            <>
              <StatsRow>
                <StatCard gradient="linear-gradient(135deg,#7C5CBF,#5B3FA0)" shadow="rgba(124,92,191,0.28)">
                  <StatText>
                    <StatNum>{detail.summary.jds_released}</StatNum>
                    <StatLbl>JDs released</StatLbl>
                  </StatText>
                  <StatIconWrap><DescriptionIcon /></StatIconWrap>
                </StatCard>
                <StatCard gradient="linear-gradient(135deg,#7C5CBF,#5B3FA0)" shadow="rgba(124,92,191,0.28)">
                  <StatText>
                    <StatNum>{detail.summary.selected}</StatNum>
                    <StatLbl>Candidates selected</StatLbl>
                  </StatText>
                  <StatIconWrap><CheckCircleIcon /></StatIconWrap>
                </StatCard>
                <StatCard gradient="linear-gradient(135deg,#7C5CBF,#5B3FA0)" shadow="rgba(124,92,191,0.28)">
                  <StatText>
                    <StatNum>{detail.summary.rejected}</StatNum>
                    <StatLbl>Candidates rejected</StatLbl>
                  </StatText>
                  <StatIconWrap><CancelIcon /></StatIconWrap>
                </StatCard>
                <StatCard gradient="linear-gradient(135deg,#7C5CBF,#5B3FA0)" shadow="rgba(124,92,191,0.28)">
                  <StatText>
                    <StatNum>{detail.summary.in_progress}</StatNum>
                    <StatLbl>Candidates in progress</StatLbl>
                  </StatText>
                  <StatIconWrap><HourglassEmptyIcon /></StatIconWrap>
                </StatCard>
              </StatsRow>

              <TableCard>
                <CardHeaderRow>
                  <Box>
                    <CardTitle>Recruiter breakdown</CardTitle>
                    <CardMeta>
                      {detail.recruiter_breakdown.length} recruiter{detail.recruiter_breakdown.length === 1 ? "" : "s"} involved
                    </CardMeta>
                  </Box>
                  <Tooltip title="Download this client's recruiter breakdown as CSV">
                    <span>
                      <ExportBtn onClick={downloadClientDetailCsv} disabled={!detail.recruiter_breakdown.length}>
                        <DownloadIcon sx={{ fontSize: 16 }} /> Export CSV
                      </ExportBtn>
                    </span>
                  </Tooltip>
                </CardHeaderRow>

                {detail.recruiter_breakdown.length === 0 ? (
                  <EmptyState
                    icon={<InboxIcon sx={{ color: "#9C7FD4", fontSize: 28 }} />}
                    title="No activity in this date range"
                    subtitle="Try widening the date range to see recruiter activity for this client."
                  />
                ) : (
                  <StyledTableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headSx}>Recruiter</TableCell>
                          <TableCell align="right" sx={headSx}>JDs released</TableCell>
                          <TableCell align="right" sx={headSx}>Selected</TableCell>
                          <TableCell align="right" sx={headSx}>Rejected</TableCell>
                          <TableCell align="right" sx={headSx}>In progress</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.recruiter_breakdown.map((r) => (
                          <TableRow key={r.recruiter_id} hover sx={{ "&:last-child td": { border: 0 } }}>
                            <TableCell>
                              <NameCell name={r.recruiter_name} subtitle={r.recruiter_email || "No email on file"} />
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.small, fontVariantNumeric: "tabular-nums", color: "#2D1B5E" }}>
                                {r.jds_released}
                              </Typography>
                            </TableCell>
                            <TableCell align="right"><CountChip value={r.selected} tone="success" /></TableCell>
                            <TableCell align="right"><CountChip value={r.rejected} tone="danger" /></TableCell>
                            <TableCell align="right"><CountChip value={r.in_progress} tone="warning" /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </StyledTableContainer>
                )}
              </TableCard>
            </>
          )}
        </>
      )}
    </PageWrapper>
    </ThemeProvider>
  );
}